import { CoreBeliefKeyType } from "api/conceptApi";
import {
  Audio,
  ButtonGroup,
  DateSelect,
  Image,
  LineWithSolidLabel,
  Percentage,
  Select,
  SelectIntermediateBelief,
  TextareaLine,
  Thoughtmap,
  TypographyFromDataLine,
} from "data/CellComponent";
import dayjs from "dayjs";
import {
  Header1,
  Instruction,
  InstructionWithContent,
  InstructionWithDataWithContent,
  InstructionWithDataWithTextarea,
  InstructionWithTextarea,
} from "../BlockComponent";
import { thoughtTrapList } from "./4-5-A";
import { CoreBeliefTemplate } from "./common";

const today = dayjs().format("YYYY-MM-DD");

export const ThoughtRecordTemplate = [
  InstructionWithContent(
    `typography_0_0_0`,
    LineWithSolidLabel("날짜", [DateSelect({ id: "date", value: today })])
  ),
  InstructionWithContent(
    `typography_1_0_0`,
    LineWithSolidLabel("상황", TextareaLine({ id: "situation" }))
  ),
  InstructionWithContent(`typography_2_0_0`, [
    ...LineWithSolidLabel("생각", TextareaLine({ id: "thought" })),
    ...LineWithSolidLabel("영향 정도", [
      Percentage({
        id: `percentageThought`,
      }),
    ]),
  ]),
  InstructionWithContent(`typography_3_0_0`, [
    ...LineWithSolidLabel(
      "감정/신체",
      TextareaLine({ id: "emotionalPhysicalResponse" })
    ),

    ...LineWithSolidLabel("행동", TextareaLine({ id: "behavior" })),
  ]),
];

const Situation = LineWithSolidLabel(
  "상황",
  TypographyFromDataLine({ A: { id: "situation" } }, `$A$`)
);
const Thought = LineWithSolidLabel(
  "생각",
  TypographyFromDataLine({ A: { id: "thought" } }, `$A$`)
);
const EmotionalPhysicalResponse = LineWithSolidLabel(
  "감정/신체",
  TypographyFromDataLine({ A: { id: "emotionalPhysicalResponse" } }, `$A$`)
);

const ThoughtTrapTemplate_1 = (upgradeState: number) => {
  return [
    InstructionWithContent(
      "upgrade1_1_typography_0_0_0",
      [
        ...Situation,

        ...Array.from({ length: 5 }, (v, i) => i + 1).flatMap((each) => [
          ...LineWithSolidLabel(`요인`, TextareaLine(), { labelIndex: each }),
        ]),
      ],
      { blockId: "생각함정 1" }
    ),
    InstructionWithTextarea(`upgrade1_1_typography_1_0_0`, undefined, {
      nextBlockId: upgradeState >= 2 ? "생각의 강" : "마무리",
    }),
  ];
};
const ThoughtTrapTemplate_2 = (upgradeState: number) => {
  return [
    InstructionWithContent(
      "upgrade1_2_typography_0_0_0",
      [
        ...Thought,
        ...LineWithSolidLabel("사실이라는 근거", TextareaLine()),
        ...LineWithSolidLabel("사실이 아니라는 근거", TextareaLine()),
      ],
      { blockId: "생각함정 2" }
    ),

    InstructionWithTextarea(`upgrade1_2_typography_1_0_0`, undefined, {
      nextBlockId: upgradeState >= 2 ? "생각의 강" : "마무리",
    }),
  ];
};
const ThoughtTrapTemplate_3 = (upgradeState: number) => {
  return [
    InstructionWithContent(
      "upgrade1_3_typography_0_0_0",
      [
        ...Thought,
        ...Array.from({ length: 5 }, (v, i) => i + 1).flatMap((each) => [
          ...LineWithSolidLabel(`다른 가능성`, TextareaLine(), {
            labelIndex: each,
          }),
        ]),
      ],
      { blockId: "생각함정 3" }
    ),
    InstructionWithTextarea(`upgrade1_3_typography_1_0_0`),
    InstructionWithTextarea(`upgrade1_3_typography_2_0_0`, undefined, {
      nextBlockId: upgradeState >= 2 ? "생각의 강" : "마무리",
    }),
  ];
};
const ThoughtTrapTemplate_4 = (upgradeState: number) => {
  return [
    InstructionWithContent(
      "upgrade1_4_typography_0_0_0",
      [
        ...Situation,
        ...Thought,
        ...LineWithSolidLabel(
          "기술 (상황과 나의 직접적인 특성)",
          TextareaLine()
        ),
        ...LineWithSolidLabel(
          "평가 (상황과 나에 대한 가치 판단)",
          TextareaLine()
        ),
      ],
      { blockId: "생각함정 4" }
    ),
    InstructionWithTextarea(`upgrade1_4_typography_1_0_0`),
    InstructionWithTextarea(`upgrade1_4_typography_2_0_0`),
    InstructionWithTextarea(`upgrade1_4_typography_3_0_0`, undefined, {
      nextBlockId: upgradeState >= 2 ? "생각의 강" : "마무리",
    }),
  ];
};
const ThoughtTrapTemplate_5 = (upgradeState: number) => {
  return [
    InstructionWithContent(
      "upgrade1_5_typography_0_0_0",
      [
        ...Thought,
        ...EmotionalPhysicalResponse,
        ...LineWithSolidLabel("개념화 (정당화하기 위한 이유)", TextareaLine()),
      ],
      { blockId: "생각함정 5" }
    ),
    InstructionWithTextarea(`upgrade1_5_typography_1_0_0`),
    InstructionWithTextarea(`upgrade1_5_typography_2_0_0`, undefined, {
      nextBlockId: upgradeState >= 2 ? "생각의 강" : "마무리",
    }),
  ];
};
const ThoughtTrapTemplate_6 = (upgradeState: number) => {
  return [
    InstructionWithContent("upgrade1_6_typography_0_0_0", [...Thought], {
      blockId: "생각함정 6",
    }),
    InstructionWithTextarea(`upgrade1_6_typography_1_0_0`),
    InstructionWithTextarea(`upgrade1_6_typography_2_0_0`),
    InstructionWithTextarea(`upgrade1_6_typography_3_0_0`, undefined, {
      nextBlockId: upgradeState >= 2 ? "생각의 강" : "마무리",
    }),
  ];
};

const ThoughtTrapTemplate = (upgradeState: number) => {
  return [
    ...ThoughtTrapTemplate_1(upgradeState),
    ...ThoughtTrapTemplate_2(upgradeState),
    ...ThoughtTrapTemplate_3(upgradeState),
    ...ThoughtTrapTemplate_4(upgradeState),
    ...ThoughtTrapTemplate_5(upgradeState),
    ...ThoughtTrapTemplate_6(upgradeState),
  ];
};

const getThoughtTrapIndex = (thoughtTrap: string) => {
  return thoughtTrapList.findIndex((each) => each === thoughtTrap);
};

export const ThoughtRecordUpgradeTemplateCommon = (
  upgradeState: number,
  thoughtTrapNumList: string[]
) => {
  return [
    Header1(`upgrade1_typography_0_0_0`),
    Instruction(`upgrade1_typography_1_0_0`),
    InstructionWithContent(`upgrade1_typography_2_0_0`, [
      ...LineWithSolidLabel(
        "그 사람의 상황",
        TypographyFromDataLine({ A: { id: "situation" } }, `$A$`)
      ),
      ...LineWithSolidLabel("그 사람의 생각", TextareaLine()),
    ]),
    InstructionWithContent(`upgrade1_typography_3_0_0`, [
      ...Thought,
      ...LineWithSolidLabel("이점", TextareaLine()),
      ...LineWithSolidLabel("손해", TextareaLine()),
    ]),
    InstructionWithContent(`upgrade1_typography_4_0_0`, [
      ...Thought,
      ...LineWithSolidLabel("생각함정", [
        Select(
          [
            ...thoughtTrapList.filter((each, index) =>
              thoughtTrapNumList
                .map((each) => parseInt(each))
                .includes(index + 1)
            ),
            "새로운 함정인 것 같아요. 도와주세요.",
          ].map((each, index) => ({
            translationKey: each,

            showFromId:
              getThoughtTrapIndex(each) === -1
                ? "모르겠음"
                : `생각함정 ${getThoughtTrapIndex(each) + 1}`,
            hideFromId:
              getThoughtTrapIndex(each) === -1
                ? "생각함정 1"
                : `생각함정 ${getThoughtTrapIndex(each) + 2}`,
          })),
          {
            id: "생각함정",
          }
        ),
      ]),
    ]),
    Instruction("upgrade1_typography_5_0_0", {
      blockId: "모르겠음",
      isEnd: true,
    }),
    ...ThoughtTrapTemplate(upgradeState),
  ];
};

export const StreamOfThoughtsTemplate = (upgradeState: number) => [
  InstructionWithContent(
    "upgrade2_typography_0_0_0",
    [
      [
        ButtonGroup([
          {
            translationKey: "5분",
            showFromId: "오디오_5분",
            hideFromId: "오디오_15분",
          },
          {
            translationKey: "15분",
            showFromId: "오디오_15분",
            hideFromId: "오디오_5분",
          },
          {
            translationKey: "없음",
            showFromId: upgradeState > 2 ? "나를 이해하기" : "마무리",
            hideFromId: "오디오_5분",
          },
        ]),
      ],
    ],
    { blockId: "생각의 강" }
  ),
  InstructionWithContent(
    "upgrade2_typography_1_0_0",
    [Image("0"), Audio("stream_of_thoughts_5min")],
    {
      blockId: "오디오_5분",
      nextBlockId: upgradeState > 2 ? "나를 이해하기" : "마무리",
    }
  ),
  InstructionWithContent(
    "upgrade2_typography_1_0_0",
    [Image("0"), Audio("stream_of_thoughts_15min")],
    {
      blockId: "오디오_15분",
      nextBlockId: upgradeState > 2 ? "나를 이해하기" : "마무리",
    }
  ),
];

export const BeliefTemplate = (coreBeliefKeyList?: CoreBeliefKeyType[]) => [
  Header1("upgrade3_typography_0_0_0", { blockId: "나를 이해하기" }),
  InstructionWithContent(
    "upgrade3_typography_1_0_0",
    CoreBeliefTemplate({ coreBeliefKeyList: coreBeliefKeyList })
  ),

  InstructionWithDataWithTextarea(
    { A: { id: "생각함정" } },
    "upgrade3_typography_2_0_0"
  ),

  InstructionWithContent(`upgrade3_typography_4_0_0`, [
    [
      // Select(
      //   [
      //     {
      //       dataMapping: { A: { id: "규칙" } },
      //       translationKey: "$A$",
      //     },
      //     {
      //       dataMapping: { A: { id: "태도" } },
      //       translationKey: "$A$",
      //     },
      //     {
      //       dataMapping: { A: { id: "가정" } },
      //       translationKey: "$A$",
      //     },
      //   ],
      //   { id: `중간믿음` }
      // ),
      SelectIntermediateBelief({ id: "중간믿음" }),
    ],
  ]),
  InstructionWithDataWithTextarea(
    { A: { id: "중간믿음" } },
    "upgrade3_typography_5_0_0"
  ),
  InstructionWithTextarea("upgrade3_typography_6_0_0"),
];

export const ResetImpactLevel = [
  InstructionWithDataWithContent(
    { A: { id: "percentageThought" } },
    `wrapup_typographyFromData_0_0_0`,
    [
      ...LineWithSolidLabel(
        "생각",
        TypographyFromDataLine({ A: { id: "thought" } }, `$A$`)
      ),
      ...LineWithSolidLabel("거리두기 후 영향 정도", [Percentage()]),
    ],
    { blockId: "마무리" }
  ),
];

export const ThoughtRecordLastCard = [Instruction("last_typography_0_0_0")];
